
import React from "react";
import moment from "moment";

const VibrantAdventureTemplate = ({ packageData, user }) => {
  if (!packageData?.package) return <div>Loading package data...</div>;
  const { package: pkg } = packageData;

  const styles = {
    container: { fontFamily: "'Montserrat', sans-serif", color: "#34495e", backgroundColor: "#f4f7f9", padding: "0" },
    hero: {
      position: 'relative', color: 'white', textAlign: 'center',
      height: '350px', display: 'flex', flexDirection: 'column',
      justifyContent: 'center', alignItems: 'center', padding: '20px'
    },
    heroImage: { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 1, filter: 'brightness(0.6)' },
    heroContent: { zIndex: 2, position: 'relative' },
    title: { fontSize: '3rem', fontWeight: '700', textShadow: '2px 2px 8px rgba(0,0,0,0.7)', color: '#ffff' },
    contentBody: { padding: '40px' },
    section: { marginBottom: '40px' },
    sectionTitle: { fontSize: '1.8rem', fontWeight: '600', color: '#2c3e50', marginBottom: '25px', position: 'relative', paddingBottom: '10px' },
    sectionTitleAfter: { content: '""', position: 'absolute', left: 0, bottom: 0, width: '60px', height: '4px', backgroundColor: '#1abc9c' },
    summaryContainer: { display: 'flex', justifyContent: 'space-around', gap: '20px', textAlign: 'center', flexWrap: 'wrap' },
    summaryCard: { backgroundColor: 'white', padding: '20px', borderRadius: '10px', boxShadow: '0 4px 15px rgba(0,0,0,0.08)', flex: 1, minWidth: '200px' },
    itineraryItem: { display: 'flex', marginBottom: '25px', alignItems: 'flex-start' },
    itineraryDay: { flexShrink: 0, marginRight: '20px', textAlign: 'center' },
    dayCircle: { width: '50px', height: '50px', borderRadius: '50%', backgroundColor: '#1abc9c', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '1.2rem' },
    itineraryContent: { backgroundColor: 'white', padding: '20px', borderRadius: '8px', width: '100%', boxShadow: '0 4px 15px rgba(0,0,0,0.05)' },
    gridContainer: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' },
    card: { backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 4px 15px rgba(0,0,0,0.08)' },
    cardTitle: { fontWeight: '600', color: '#16a085', marginBottom: '10px' },
    list: { padding: 0, listStyle: 'none' },
    listItem: { display: 'flex', alignItems: 'center', marginBottom: '10px' },
    listItemIcon: { color: '#1abc9c', marginRight: '10px', fontSize: '1.2rem' },
    footer: { textAlign: 'center', padding: '30px', backgroundColor: '#2c3e50', color: '#ecf0f1' }
  };

  const SectionHeader = ({ title }) => (
    <h2 style={styles.sectionTitle}>{title}<span style={styles.sectionTitleAfter}></span></h2>
  );

  return (
    <div style={styles.container}>
      <header style={styles.hero}>
        <img src={pkg.imageSrc} alt={pkg.packageName} style={styles.heroImage} />
        <div style={styles.heroContent}>
          {user?.logo && <img src={user.logo} alt="Logo" style={{ maxHeight: '50px', background: 'rgba(255,255,255,0.8)', padding: '5px', borderRadius: '5px', marginBottom: '15px' }} />}
          <h1 style={styles.title}>{pkg.packageName}</h1>
          <p style={{ fontSize: '1.4rem' }}>{pkg.destinationsCover}</p>
        </div>
      </header>
      
      <main style={styles.contentBody}>
        <section style={styles.section}>
          <div style={styles.summaryContainer}>
            <div style={styles.summaryCard}>
              <div style={{ fontSize: '2rem' }}>🕒</div>
              <strong>Duration</strong><br/>{pkg.duration}
            </div>
            <div style={styles.summaryCard}>
              <div style={{ fontSize: '2rem' }}>📅</div>
              <strong>Start Date</strong><br/>{moment(pkg.startedDate).format("MMM Do, YYYY")}
            </div>
            <div style={styles.summaryCard}>
              <div style={{ fontSize: '2rem' }}>💰</div>
              <strong>Price</strong><br/>{pkg.price} {pkg.currency}
            </div>
          </div>
        </section>

        <section style={styles.section}>
          <SectionHeader title="About The Trip" />
          <p style={{ lineHeight: '1.7' }}>{pkg.description}</p>
        </section>

        {packageData.itineraries?.length > 0 && (
          <section style={styles.section}>
            <SectionHeader title="Your Journey" />
            {packageData.itineraries.slice().sort((a, b) => new Date(a.date) - new Date(b.date)).map((item, index) => (
              <div key={index} style={styles.itineraryItem}>
                <div style={styles.itineraryDay}><div style={styles.dayCircle}>Day {index + 1}</div></div>
                <div style={styles.itineraryContent}>
                  <h4 style={{ color: '#16a085', fontWeight: '600', margin: '0 0 10px 0' }}>{item.title}</h4>
                  <p style={{ margin: 0 }}>{item.description}</p>
                </div>
              </div>
            ))}
          </section>
        )}

        {/* --- TRAVEL LOGISTICS: FLIGHTS & HOTELS --- */}
        {(packageData.flightBookings?.length > 0 || packageData.hotelBookings?.length > 0) && (
          <section style={styles.section}>
            <SectionHeader title="Travel & Stays" />
            <div style={styles.gridContainer}>
              {packageData.flightBookings?.map((flight, i) => (
                <div key={`flight-${i}`} style={styles.card}>
                  <h4 style={styles.cardTitle}>✈️ Flight Details</h4>
                  <p><strong>{flight.departureCity} → {flight.arrivalCity}</strong></p>
                  <p>{flight.airline} {flight.flightNumber}</p>
                  <p style={{ fontSize: '0.9rem', color: '#7f8c8d' }}>{moment(flight.departureDateTime).format('DD MMM, HH:mm')}</p>
                </div>
              ))}
              {packageData.hotelBookings?.map((hotel, i) => (
                <div key={`hotel-${i}`} style={styles.card}>
                  <h4 style={styles.cardTitle}>🏨 Accommodation</h4>
                  <p><strong>{hotel.hotelName}</strong></p>
                  <p>{hotel.city}</p>
                  <p style={{ fontSize: '0.9rem', color: '#7f8c8d' }}>{moment(hotel.checkInDate).format('DD MMM')} - {moment(hotel.checkOutDate).format('DD MMM YYYY')}</p>
                </div>
              ))}
            </div>
          </section>
        )}
        
        {/* --- SIGHTSEEING & TRANSFERS --- */}
        {(packageData.sightSeeings?.length > 0 || packageData.transfers?.length > 0) && (
          <section style={styles.section}>
            <SectionHeader title="Activities & Transfers" />
            <div style={{...styles.card, display: 'flex', gap: '30px', flexWrap: 'wrap' }}>
              {packageData.sightSeeings?.length > 0 && (
                <div style={{ flex: 1, minWidth: '250px' }}>
                  <h4 style={styles.cardTitle}>📸 Sightseeing</h4>
                  <ul style={styles.list}>
                    {packageData.sightSeeings.map((sight, i) => <li key={i}>{sight.sightseeingName}</li>)}
                  </ul>
                </div>
              )}
              {packageData.transfers?.length > 0 && (
                <div style={{ flex: 1, minWidth: '250px' }}>
                  <h4 style={styles.cardTitle}>🚗 Transfers</h4>
                   <ul style={styles.list}>
                    {packageData.transfers.map((transfer, i) => <li key={i}>{transfer.fromLocation} to {transfer.toLocation}</li>)}
                  </ul>
                </div>
              )}
            </div>
          </section>
        )}

        {/* --- INCLUSIONS & EXCLUSIONS --- */}
        {(packageData.includes?.length > 0 || packageData.excludes?.length > 0) && (
          <section style={styles.section}>
            <div style={{ display: 'flex', gap: '30px', flexWrap: 'wrap' }}>
              {packageData.includes?.length > 0 && (
                <div style={{ ...styles.card, flex: 1, minWidth: '300px' }}>
                  <h3 style={styles.cardTitle}>What's Included</h3>
                  <ul style={styles.list}>
                    {packageData.includes.map((item, i) => (
                      <li key={i} style={styles.listItem}><span style={styles.listItemIcon}>✔️</span>{item.description}</li>
                    ))}
                  </ul>
                </div>
              )}
              {packageData.excludes?.length > 0 && (
                <div style={{ ...styles.card, flex: 1, minWidth: '300px' }}>
                  <h3 style={styles.cardTitle}>What's Not Included</h3>
                   <ul style={styles.list}>
                    {packageData.excludes.map((item, i) => (
                      <li key={i} style={styles.listItem}><span style={{...styles.listItemIcon, color: '#e74c3c'}}>❌</span>{item.description}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </section>
        )}
        
        {/* --- TERMS & CONDITIONS --- */}
        {packageData.termsandConditions?.length > 0 && (
          <section style={styles.section}>
            <SectionHeader title="Terms & Conditions" />
            <div style={styles.card}>
              {packageData.termsandConditions.map((term, i) => (
                <div key={i} style={{ marginBottom: '15px' }}>
                  <h4 style={{ fontWeight: '600', fontSize: '1rem' }}>{term.title}</h4>
                  <p style={{ fontSize: '0.9rem', color: '#7f8c8d' }}>{term.description}</p>
                </div>
              ))}
            </div>
          </section>
        )}

      </main>
      
      <footer style={styles.footer}>
        <p><strong>Questions?</strong> We're here to help!</p>
        <p>Contact us at {pkg.contactEmail} or {pkg.contactPhoneNumber}</p>
      </footer>
    </div>
  );
};

export default VibrantAdventureTemplate;